import { useState, useEffect } from "react";
import { Container, Paper, FormControl, InputLabel, Select, MenuItem } from "@mui/material";

import Product from "../../Services/FoodCost";
import Stock from "../../Services/Stock";
import DailyServings from "../../Services/DailyServing";

import { DailyServingsAnalytics } from "../../Components/FoodCost/DailyServingsAnalytics";

const DailyServingsAnalyticsPage = () => {
  const [products, setProducts] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [form, setForm] = useState({ 
    name: "", 
    soldPrice: "", 
    ingredients: [],
    unit: 'piece' // Ajout de l'unité par défaut
  });
  const [editingProduct, setEditingProduct] = useState(null);
  const [dailyServings, setDailyServings] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [currentServing, setCurrentServing] = useState({ 
    id: null, 
    items: [], 
    total: 0 
  });

  const restaurantId = localStorage.getItem("restaurantId");

  useEffect(() => {
    fetchProducts();
    fetchStocks();
    fetchDailyServings();
  }, []);
  const handleProductSubmit = async (formData) => {
    try {
      const validIngredients = formData.ingredients.map(ingredient => ({
        ...ingredient,
        unit: Stock.normalizeUnit(ingredient.unit),
        quantity: Number(ingredient.quantity)
      }));

      const productData = {
        ...formData,
        soldPrice: parseFloat(formData.soldPrice),
        ingredients: validIngredients,
        unit: Stock.normalizeUnit(formData.unit)
      };

      const product = new Product(
        productData.name,
        productData.soldPrice,
        restaurantId,
        "",
        productData.ingredients,
        productData.unit
      );

      if (editingProduct) {
        await product.updateProduct(editingProduct, productData);
      } else {
        await product.addProduct();
      }
      
      fetchProducts();
      setForm({ name: "", soldPrice: "", ingredients: [], unit: 'piece' });
      setEditingProduct(null);
    } catch (error) {
      console.error("Error saving product:", error);
    }
  };
  // Data fetching functions
  const fetchProducts = async () => {
    try {
      const data = await Product.getAllProducts();
      const filteredData = data.filter(p => p.restaurantId === restaurantId);
      setProducts(filteredData);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchStocks = async () => {
    try {
      const data = await Stock.getAllStocks(restaurantId);
      setStocks(data);
    } catch (error) {
      console.error("Error fetching stocks:", error);
    }
  };

  const fetchDailyServings = async () => {
    try {
      const data = await DailyServings.getDailyServingsByRestaurantId(restaurantId);
      const normalizedData = data.map(serving => ({
        id: serving.id,
        items: serving.items || [],
        totalCost: parseFloat(serving.totalCost) || 0,
        realCost: parseFloat(serving.realCost) || 0,
        date: serving.date || new Date().toISOString(),
        restaurantId: serving.restaurantId
      }));
      setDailyServings(normalizedData);
    } catch (error) {
      console.error("Error fetching daily servings:", error);
    }
  };

  // Product handlers
  const handleEdit = (product) => {
    setEditingProduct(product.id);
    setForm({
      ...product,
      unit: product.unit || 'piece',
      ingredients: product.ingredients.map(ing => ({
        ...ing,
        unit: Stock.normalizeUnit(ing.unit) // Normalisation
      }))
    });
  };

  const handleDelete = async (id) => {
    await new Product().deleteProduct(id);
    fetchProducts();
  };

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleUnitChange = (e) => {
    setForm({ ...form, unit: Stock.normalizeUnit(e.target.value) });
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...form.ingredients];
    
    // Auto-complétion de l'unité si le stock est sélectionné
    if (field === 'stockName') {
      const selectedStock = stocks.find(s => s.productName === value);
      if (selectedStock) {
        newIngredients[index] = {
          ...newIngredients[index],
          [field]: value,
          unit: Stock.normalizeUnit(selectedStock.unit)
        };
      }
    } else {
      newIngredients[index][field] = value;
    }
    
    setForm({ ...form, ingredients: newIngredients });
  };

  const addIngredient = () => {
    setForm({ 
      ...form, 
      ingredients: [...form.ingredients, { 
        stockName: "", 
        quantity: 0,
        unit: 'piece' // Valeur par défaut
      }] 
    });
  };

  const removeIngredient = (index) => {
    const newIngredients = form.ingredients.filter((_, i) => i !== index);
    setForm({ ...form, ingredients: newIngredients });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Validation et normalisation
      const validIngredients = form.ingredients.map(ingredient => ({
        ...ingredient,
        unit: Stock.normalizeUnit(ingredient.unit),
        quantity: Number(ingredient.quantity)
      }));

      const productData = {
        ...form,
        soldPrice: parseFloat(form.soldPrice),
        ingredients: validIngredients,
        unit: Stock.normalizeUnit(form.unit)
      };

      const product = new Product(
        productData.name,
        productData.soldPrice,
        restaurantId,
        "",
        productData.ingredients,
        productData.unit // Ajout de l'unité
      );

      if (editingProduct) {
        await product.updateProduct(editingProduct, productData);
      } else {
        await product.addProduct();
      }
      
      fetchProducts();
      setForm({ name: "", soldPrice: "", ingredients: [], unit: 'piece' });
      setEditingProduct(null);
    } catch (error) {
      console.error("Error saving product:", error);
    }
  };
  // Daily serving handlers
  const handleProductSelect = (product) => {
    if (!selectedProducts.some(p => p.id === product.id)) {
      const updatedProducts = [...selectedProducts, { ...product, quantity: 1 }];
      setSelectedProducts(updatedProducts);
      updateTotal(updatedProducts);
    }
  };

  const updateQuantity = (id, quantity) => {
    const updated = selectedProducts.map(p => 
      p.id === id ? { ...p, quantity: Math.max(1, quantity) } : p
    );
    setSelectedProducts(updated);
    updateTotal(updated);
  };

  const updateTotal = (items) => {
    const total = items.reduce((sum, item) => sum + (item.costPrice * item.quantity), 0);
    setCurrentServing(prev => ({ ...prev, items, total }));
  };

  const removeProduct = (id) => {
    const filtered = selectedProducts.filter(p => p.id !== id);
    setSelectedProducts(filtered);
    updateTotal(filtered);
  };

  const addDailyServing = async () => {
    try {
      if (selectedProducts.length === 0) {
        alert("Please select at least one product");
        return;
      }

      const items = selectedProducts.map(p => ({
        productId: p.id,
        productName: p.name,
        quantity: p.quantity,
        costPrice: p.costPrice,
        soldPrice: p.soldPrice
      }));

      const totalCost = items.reduce((sum, item) => sum + (item.costPrice * item.quantity), 0);
      const realCost = items.reduce((sum, item) => sum + (item.soldPrice * item.quantity), 0);
      const date = new Date().toISOString();

      const newServing = new DailyServings(
        items,
        totalCost,
        realCost,
        date,
        restaurantId
      );

      await newServing.addDailyServing();
      await fetchDailyServings();
      resetServingForm();
    } catch (error) {
      console.error("Error saving daily serving:", error);
    }
  };


  const editDailyServing = (serving) => {
    setCurrentServing({
      id: serving.id,
      items: serving.items,
      totalCost: serving.totalCost,
      realCost: serving.realCost
    });
    
    setSelectedProducts(serving.items.map(item => ({
      id: item.productId,
      name: item.productName,
      quantity: item.quantity,
      costPrice: item.costPrice,
      soldPrice: item.soldPrice
    })));
  };

  const deleteDailyServing = async (id) => {
    try {
      await DailyServings.deleteDailyServing(id);
      setDailyServings(dailyServings.filter(s => s.id !== id));
    } catch (error) {
      console.error("Error deleting daily serving:", error);
    }
  };

  const resetServingForm = () => {
    setCurrentServing({ id: null, items: [], total: 0 });
    setSelectedProducts([]);
  };

  return (

     <DailyServingsAnalytics 
         dailyServings={dailyServings}
        
         />
  );
};

export default DailyServingsAnalyticsPage;